import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getUsuarioById, createUsuario, updateUsuario } from '../services/usuarioService';
import { getRoles } from '../services/rolService';
import { getSucursales } from '../services/sucursalService';
import { ArrowLeft, Save, User, Key, Mail, Shield, UserCheck, UserX, Landmark } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';

export default function UsuarioForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState([]);
    const [formData, setFormData] = useState({
        nombre_usuario: '', // Solo para creación
        contrasena: '',
        email: '',
        nombre: '',
        apellido: '',
        rol: '', // Solo para creación (ID simple)
        rolIds: [], // Para edición (múltiples IDs)
        estadoValor: 'ACT',
        sucursalId: ''
    });

    const [sucursales, setSucursales] = useState([]);

    useEffect(() => {
        const cargarDatos = async () => {
            try {
                let listaRoles = [];
                let listaSucursales = [];
                let usuarioData = null;

                try {
                    const results = await Promise.allSettled([
                        getRoles(),
                        getSucursales(),
                        isEditing ? getUsuarioById(id) : Promise.resolve(null)
                    ]);

                    if (results[0].status === 'fulfilled') listaRoles = results[0].value;
                    else throw new Error('Error al obtener roles');

                    if (results[1].status === 'fulfilled') {
                        listaSucursales = results[1].value;
                    } else if (results[1].reason?.message?.includes('403') || results[1].reason?.toString()?.includes('403')) {
                        toast.error('No tienes permiso para ver Sucursales (sucursales.read)');
                        console.warn('Falta permiso sucursales.read para poblar el dropdown');
                    } else {
                        console.error('Error al obtener sucursales:', results[1].reason);
                    }

                    if (results[2].status === 'fulfilled') usuarioData = results[2].value;
                    else if (isEditing) throw new Error('Error al obtener datos del usuario');

                } catch (pe) {
                    console.error('Error crítico en carga:', pe);
                    toast.error(pe.message || 'Error al cargar datos');
                }

                setRoles(listaRoles);
                setSucursales(listaSucursales);
                if (usuarioData) {
                    // Mapeo selectivo para edición
                    setFormData({
                        nombre_usuario: usuarioData.nombreUsuario,
                        email: usuarioData.email || '',
                        nombre: usuarioData.nombre || '',
                        apellido: usuarioData.apellido || '',
                        rolIds: usuarioData.rolIds || [], // Deberíamos asegurar que el backend devuelva IDs
                        estadoValor: usuarioData.estado ? 'ACT' : 'BAJ',
                        contrasena: '', // Limpia para no mostrar el hash
                        sucursalId: usuarioData.sucursalId || ''
                    });

                    // Si el backend no devuelve IDs, buscamos por nombres en la lista de roles
                    if (usuarioData.roles && (!usuarioData.rolIds || usuarioData.rolIds.length === 0)) {
                        const ids = listaRoles
                            .filter(r => usuarioData.roles.includes(r.nombre))
                            .map(r => r.id);
                        setFormData(prev => ({ ...prev, rolIds: ids }));
                    }
                }
            } catch (error) {
                console.error('Error al cargar datos:', error);
                toast.error('Error al cargar la información del usuario');
            } finally {
                setLoading(false);
            }
        };

        cargarDatos();
    }, [id, isEditing]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleToggleRol = (rolId) => {
        if (!isEditing) {
            setFormData(prev => ({ ...prev, rol: rolId.toString() }));
        } else {
            setFormData(prev => {
                const exists = prev.rolIds.includes(rolId);
                if (exists) {
                    return { ...prev, rolIds: prev.rolIds.filter(rid => rid !== rolId) };
                } else {
                    return { ...prev, rolIds: [...prev.rolIds, rolId] };
                }
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validación previa en el frontend
        if (!isEditing && formData.contrasena.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        if (!isEditing && !formData.rol) {
            toast.error('Debes seleccionar un rol para el nuevo usuario');
            return;
        }

        try {
            setLoading(true);
            const dataToSend = {
                nombre_usuario: formData.nombre_usuario,
                nombre: formData.nombre,
                apellido: formData.apellido,
                contrasena: formData.contrasena,
                correo: formData.email,
                rol: formData.rol,
                sucursalId: formData.sucursalId === '' ? null : Number(formData.sucursalId)
            };

            if (isEditing) {
                await updateUsuario(id, {
                    nombre: formData.nombre,
                    apellido: formData.apellido,
                    email: formData.email,
                    contrasena: formData.contrasena,
                    rolIds: formData.rolIds,
                    estadoValor: formData.estadoValor,
                    sucursalId: formData.sucursalId === '' ? null : Number(formData.sucursalId)
                });
                toast.success('Usuario actualizado exitosamente');
            } else {
                await createUsuario(dataToSend);
                toast.success('Usuario creado exitosamente');
            }
            setTimeout(() => navigate('/seguridad/usuarios'), 1000);
        } catch (error) {
            console.error('Error al guardar:', error);
            
            // Si el backend devolvió errores de validación específicos
            if (error.errors && typeof error.errors === 'object') {
                const errorMessages = Object.values(error.errors);
                errorMessages.forEach(msg => toast.error(msg, { duration: 5000 }));
            } else {
                toast.error(error.message || 'Error al guardar el usuario');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto font-sans">
            <Toaster position="top-right" />

            <div className="flex items-center justify-between mb-6 bg-white p-4 shadow-sm border border-gray-100 rounded-lg">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/seguridad/usuarios')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-gray-800 tracking-tight">
                            {isEditing ? `Editar Usuario: ${formData.nombre_usuario}` : 'Nuevo Usuario Operador'}
                        </h1>
                        <p className="text-sm text-gray-500 font-medium italic">Configura el acceso y perfil del personal</p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Columna Izquierda: Credenciales y Perfil */}
                <div className="md:col-span-2 space-y-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <div className="flex items-center gap-2 mb-6 pb-2 border-b border-gray-100">
                            <User className="w-5 h-5 text-blue-600" />
                            <h2 className="font-bold text-gray-700 uppercase text-xs tracking-widest">Información de la Cuenta</h2>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[0.65rem] font-bold text-gray-400 uppercase mb-1">Usuario (Login)</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        name="nombre_usuario"
                                        value={formData.nombre_usuario}
                                        onChange={handleChange}
                                        disabled={isEditing}
                                        className="w-full p-2 pl-8 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none text-sm font-mono disabled:bg-gray-50 disabled:text-gray-400"
                                        required
                                    />
                                    <User className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[0.65rem] font-bold text-gray-400 uppercase mb-1">E-mail</label>
                                <div className="relative">
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full p-2 pl-8 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none text-sm"
                                        required
                                    />
                                    <Mail className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[0.65rem] font-bold text-gray-400 uppercase mb-1">Nombre(s)</label>
                                <input
                                    type="text"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none text-sm uppercase"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-[0.65rem] font-bold text-gray-400 uppercase mb-1">Apellido(s)</label>
                                <input
                                    type="text"
                                    name="apellido"
                                    value={formData.apellido}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none text-sm uppercase"
                                    required
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-[0.65rem] font-bold text-gray-400 uppercase mb-1">
                                    {isEditing ? 'Nueva Contraseña (dejar en blanco para mantener)' : 'Contraseña Inicial'}
                                </label>
                                <div className="relative">
                                    <input
                                        type="password"
                                        name="contrasena"
                                        value={formData.contrasena}
                                        onChange={handleChange}
                                        minLength={isEditing ? 0 : 6}
                                        placeholder={isEditing ? "(Sin cambios)" : "Mínimo 6 caracteres"}
                                        className="w-full p-2 pl-8 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none text-sm transition-all focus:border-blue-500"
                                        required={!isEditing}
                                    />
                                    <Key className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                                </div>
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-[0.65rem] font-bold text-gray-400 uppercase mb-1">Sucursal de Trabajo</label>
                                <div className="relative">
                                    <select
                                        name="sucursalId"
                                        value={formData.sucursalId}
                                        onChange={handleChange}
                                        className="w-full p-2 pl-8 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none text-sm appearance-none bg-white font-medium"
                                        required
                                    >
                                        <option value="">-- SELECCIONAR SUCURSAL --</option>
                                        {sucursales.map(s => (
                                            <option key={s.id} value={s.id}>{s.nombre} ({s.codigoEstablecimiento})</option>
                                        ))}
                                    </select>
                                    <Landmark className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                                </div>
                                <p className="text-[0.6rem] text-slate-400 bg-slate-50 p-2 mt-1 rounded italic">El usuario solo podrá operar cajas y ver movimientos vinculados a esta sucursal.</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <div className="flex items-center gap-2 mb-6 pb-2 border-b border-gray-100">
                            <Shield className="w-5 h-5 text-blue-600" />
                            <h2 className="font-bold text-gray-700 uppercase text-xs tracking-widest">Asignación de Roles</h2>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {roles.map(rol => (
                                <div
                                    key={rol.id}
                                    onClick={() => handleToggleRol(rol.id)}
                                    className={`p-3 border rounded cursor-pointer transition-all flex items-center justify-between group ${(!isEditing && formData.rol === rol.id.toString()) || (isEditing && formData.rolIds.includes(rol.id))
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    <div className="flex flex-col">
                                        <span className="text-xs font-bold text-gray-800 font-mono italic">
                                            {rol.nombre}
                                        </span>
                                        <span className="text-[0.6rem] text-gray-400 leading-tight">
                                            {rol.descripcion || 'Acceso general'}
                                        </span>
                                    </div>
                                    {((!isEditing && formData.rol === rol.id.toString()) || (isEditing && formData.rolIds.includes(rol.id))) && (
                                        <CheckCircle2 className="w-4 h-4 text-blue-600" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Columna Derecha: Estado y Guardado */}
                <div className="md:col-span-1 space-y-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                            <Settings className="w-5 h-5 text-gray-600" />
                            <h2 className="font-bold text-gray-700 uppercase text-xs tracking-widest">Estado y Acciones</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-[0.65rem] font-bold text-gray-400 uppercase mb-2">Estado de la Cuenta</label>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, estadoValor: 'ACT' }))}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2 border rounded transition-all text-xs font-bold ${formData.estadoValor === 'ACT'
                                            ? 'bg-green-50 border-green-500 text-green-700 shadow-inner'
                                            : 'bg-white border-gray-200 text-gray-400 opacity-60'
                                            }`}
                                    >
                                        <UserCheck className="w-4 h-4" />
                                        ACTIVO
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, estadoValor: 'BAJ' }))}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2 border rounded transition-all text-xs font-bold ${formData.estadoValor === 'BAJ'
                                            ? 'bg-red-50 border-red-500 text-red-700 shadow-inner'
                                            : 'bg-white border-gray-200 text-gray-400 opacity-60'
                                            }`}
                                    >
                                        <UserX className="w-4 h-4" />
                                        BAJA
                                    </button>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-bold py-3 px-4 rounded transition-all disabled:opacity-50 mt-4 h shadow-lg tracking-widest uppercase text-xs"
                            >
                                <Save className="w-4 h-4" />
                                {loading ? 'Sincronizando...' : isEditing ? 'Actualizar Cuenta' : 'Registrar Operador'}
                            </button>
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 border border-blue-100 rounded-lg">
                        <h4 className="text-blue-800 font-bold text-xs mb-2 uppercase tracking-tight flex items-center gap-2">
                            Ayuda del Sistema
                        </h4>
                        <p className="text-blue-700 text-[0.7rem] leading-relaxed italic">
                            Asegúrate de asignar al menos un rol para que el usuario pueda navegar. Los cambios de permisos se aplican al próximo inicio de sesión del operador.
                        </p>
                    </div>
                </div>
            </form>
        </div>
    );
}

// Iconos adicionales no importados antes
const Settings = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path><circle cx="12" cy="12" r="3"></circle></svg>
);

const CheckCircle2 = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path><path d="m9 12 2 2 4-4"></path></svg>
);
